import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Settings,
} from 'lucide-react'
import { useNavigationBar } from '../hooks/useNavigationBar'
import mccLogo from '@/assets/mcc_logo.png'
import { roles } from '@/features/users/constants/roles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserRoleEnum } from '@/features/users/types/user'

export default function Sidebar() {
  const {
    location,
    navigate,
    user,
    activeRole,
    logout,
    handleRoleChange,
    getVisibleHomeItem,
    getVisibleSettingsItems,
    getNavigationGroups,
    deriveGroupFromPath,
    isPathActive,
  } = useNavigationBar()

  const [expandedGroup, setExpandedGroup] = useState<
    'donations' | 'admin' | 'inventory' | 'expenses' | null
  >(null)

  useEffect(() => {
    setExpandedGroup(deriveGroupFromPath())
  }, [location.pathname])

  const toggleGroup = (
    group: 'donations' | 'admin' | 'inventory' | 'expenses',
  ) => {
    setExpandedGroup((prev) => (prev === group ? null : group))
  }

  const activeRoleDisplay =
    roles.find((r) => r._id === activeRole)?.name || 'Unknown Role'
  const activeRoleDisplayWithAdmin =
    activeRole === UserRoleEnum.PASTOR && user?.isAdminPastor
      ? `${activeRoleDisplay} (Admin)`
      : activeRoleDisplay

  const userRoles =
    user?.roles
      .map((roleId) => roles.find((r) => r._id === roleId))
      .filter(Boolean) || []

  const homeItem = getVisibleHomeItem()
  const navigationGroups = getNavigationGroups()
  const settingsItems = getVisibleSettingsItems()

  return (
    <aside className="flex min-h-screen flex-col border-r bg-white">
      <div className="px-5 mt-5 flex justify-center">
        <img src={mccLogo} alt="Logo" className="h-14 w-auto" />
      </div>
      <nav className="flex-1 px-2 mt-8">
        {/* Home */}
        {homeItem && (
          <Link
            to={homeItem.to}
            className={`my-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-neutral-100 ${
              isPathActive(homeItem.to) ? 'bg-neutral-100 font-semibold' : ''
            }`}
          >
            <span>{homeItem.icon}</span>
            <span>{homeItem.label}</span>
          </Link>
        )}

        {/* Navigation Groups */}
        {navigationGroups.map((group) => (
          <div key={group.id} className="mt-4">
            <button
              type="button"
              onClick={() => toggleGroup(group.id)}
              className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-neutral-100"
            >
              <span className="flex items-center gap-2">
                {group.icon}
                <span>{group.label}</span>
              </span>
              {expandedGroup === group.id ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
            {expandedGroup === group.id && (
              <div className="mt-1">
                {group.items.map((item) => {
                  const active = isPathActive(item.to)
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`my-1 ml-6 flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-neutral-100 ${
                        active ? 'bg-neutral-100 font-semibold' : ''
                      }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </nav>
      <div className="mt-auto border-t p-3">
        <div className="mb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-neutral-100 hover:cursor-pointer"
                type="button"
              >
                <Settings size={20} /> <span>Settings</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end">
              {settingsItems.map((item) => (
                <DropdownMenuItem
                  key={item.to}
                  className="cursor-pointer"
                  onClick={() => {
                    navigate({ to: item.to })
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button
          className="mb-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-neutral-100 hover:cursor-pointer"
          onClick={async (e) => {
            e.preventDefault()
            await logout()
          }}
        >
          <LogOut size={20} /> <span>Logout</span>
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm flex-1">
              <div className="font-medium">{user?.name ?? 'User'}</div>
              {userRoles.length > 1 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="text-neutral-500 hover:text-neutral-700 transition-colors duration-200 text-left cursor-pointer flex items-center gap-1"
                      type="button"
                    >
                      <span>{activeRoleDisplayWithAdmin}</span>
                      <ChevronsUpDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="start">
                    {userRoles.map((role) => (
                      <DropdownMenuItem
                        key={role!._id}
                        className="cursor-pointer"
                        onClick={() => handleRoleChange(role!._id)}
                      >
                        <span
                          className={
                            activeRole === role!._id ? 'font-semibold' : ''
                          }
                        >
                          {role!.name}
                          {role!._id === UserRoleEnum.PASTOR &&
                          user?.isAdminPastor
                            ? ' (Admin)'
                            : ''}
                        </span>
                        {activeRole === role!._id && (
                          <span className="ml-auto text-xs">✓</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="text-neutral-500 transition-colors duration-200">
                  {activeRoleDisplayWithAdmin}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
